import { 
  AppError, 
  ErrorContext, 
  ErrorLogger, 
  ErrorHandler, 
  ErrorRecoveryStrategy,
  ErrorReportingService,
  ErrorSeverity,
  ErrorCategory,
  errorUtils
} from './errors';
import { errorMessageUtils } from './errorMessages';

// Console-based error logger implementation
export class ConsoleErrorLogger implements ErrorLogger {
  log(error: AppError, context?: ErrorContext): void {
    const severity = errorUtils.getSeverity(error, context);
    const category = errorUtils.getCategory(error.code);
    
    const logEntry = {
      timestamp: new Date().toISOString(),
      errorId: errorUtils.generateErrorId(),
      severity,
      category,
      code: error.code,
      message: error.message,
      details: error.details,
      field: error.field,
      context: context || {},
      stack: error.stack
    };

    switch (severity) {
      case ErrorSeverity.CRITICAL:
        console.error('🚨 CRITICAL ERROR:', logEntry);
        break;
      case ErrorSeverity.HIGH:
        console.error('❌ HIGH ERROR:', logEntry);
        break;
      case ErrorSeverity.MEDIUM:
        console.warn('⚠️ MEDIUM ERROR:', logEntry);
        break;
      case ErrorSeverity.LOW:
        console.info('ℹ️ LOW ERROR:', logEntry);
        break;
    }
  }

  logError(error: Error, context?: ErrorContext): void {
    console.error('🔴 ERROR:', {
      timestamp: new Date().toISOString(),
      errorId: errorUtils.generateErrorId(),
      name: error.name,
      message: error.message,
      stack: error.stack,
      context: context || {}
    });
  }

  logWarning(message: string, context?: ErrorContext): void {
    console.warn('⚠️ WARNING:', {
      timestamp: new Date().toISOString(),
      message,
      context: context || {}
    });
  }

  logInfo(message: string, context?: ErrorContext): void {
    console.info('ℹ️ INFO:', {
      timestamp: new Date().toISOString(),
      message,
      context: context || {}
    });
  }
}

// Local storage error logger for persistence
export class LocalStorageErrorLogger implements ErrorLogger {
  private readonly storageKey = 'app_errors';
  private readonly maxErrors = 100;

  private getStoredErrors(): any[] {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  private storeErrors(errors: any[]): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(errors));
    } catch (error) {
      console.warn('Failed to store error in localStorage:', error);
    }
  }

  log(error: AppError, context?: ErrorContext): void {
    const errors = this.getStoredErrors();
    const errorEntry = {
      timestamp: new Date().toISOString(),
      errorId: errorUtils.generateErrorId(),
      code: error.code,
      message: error.message,
      details: error.details,
      field: error.field,
      context: context || {},
      stack: error.stack
    };

    errors.unshift(errorEntry);
    
    // Keep only the latest errors
    if (errors.length > this.maxErrors) {
      errors.splice(this.maxErrors);
    }

    this.storeErrors(errors);
  }

  logError(error: Error, context?: ErrorContext): void {
    const errors = this.getStoredErrors();
    const errorEntry = {
      timestamp: new Date().toISOString(),
      errorId: errorUtils.generateErrorId(),
      name: error.name,
      message: error.message,
      stack: error.stack,
      context: context || {}
    };

    errors.unshift(errorEntry);
    
    if (errors.length > this.maxErrors) {
      errors.splice(this.maxErrors);
    }

    this.storeErrors(errors);
  }

  logWarning(message: string, context?: ErrorContext): void {
    // Warnings are not stored in localStorage
    console.warn('⚠️ WARNING:', { message, context });
  }

  logInfo(message: string, context?: ErrorContext): void {
    // Info messages are not stored in localStorage
    console.info('ℹ️ INFO:', { message, context });
  }

  // Clear stored errors
  clearStoredErrors(): void {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (error) {
      console.warn('Failed to clear errors from localStorage:', error);
    }
  }
}

// Error recovery strategies
export class NetworkErrorRecoveryStrategy implements ErrorRecoveryStrategy {
  canRecover(error: AppError): boolean {
    return errorUtils.getCategory(error.code) === ErrorCategory.NETWORK;
  }

  async recover(error: AppError, context?: ErrorContext): Promise<void> {
    // Implement network retry logic
     
    
    // Simulate retry delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // In a real implementation, you might:
    // 1. Check network connectivity
    // 2. Retry the failed request
    // 3. Switch to offline mode if needed
  }
}

export class AuthenticationErrorRecoveryStrategy implements ErrorRecoveryStrategy {
  canRecover(error: AppError): boolean {
    return errorUtils.getCategory(error.code) === ErrorCategory.AUTHENTICATION;
  }

  async recover(error: AppError, context?: ErrorContext): Promise<void> {
     
    
    // In a real implementation, you might:
    // 1. Refresh the authentication token
    // 2. Redirect to login page
    // 3. Clear invalid session data
  }
}

// Error reporting service implementation
export class ConsoleErrorReportingService implements ErrorReportingService {
  private userInfo?: Record<string, any>;
  private globalContext?: Record<string, any>;

  async report(error: AppError, context?: ErrorContext): Promise<void> {
    const reportData = {
      errorId: errorUtils.generateErrorId(),
      timestamp: new Date().toISOString(),
      error: errorUtils.sanitize(error).toJSON(),
      context: {
        ...this.globalContext,
        ...context
      },
      user: this.userInfo,
      environment: {
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString()
      }
    };

    console.group('📊 ERROR REPORT');
     
     
     
     
     
    console.groupEnd();

    // In a real implementation, you would send this to your error reporting service
    // await fetch('/api/errors', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(reportData)
    // });
  }

  async reportError(error: Error, context?: ErrorContext): Promise<void> {
    const appError = new AppError(
      'UNKNOWN_ERROR',
      error.message,
      error.stack,
      undefined,
      context
    );
    await this.report(appError, context);
  }

  setUser(userId: string, userInfo?: Record<string, any>): void {
    this.userInfo = { userId, ...userInfo };
  }

  setContext(context: Record<string, any>): void {
    this.globalContext = context;
  }
}

// Main error handler implementation
export class AppErrorHandler implements ErrorHandler {
  private logger: ErrorLogger;
  protected reportingService: ErrorReportingService;
  private recoveryStrategies: ErrorRecoveryStrategy[];
  private retryAttempts: Map<string, number> = new Map();
  private readonly maxRetryAttempts = 3;

  constructor(
    logger: ErrorLogger = new ConsoleErrorLogger(),
    reportingService: ErrorReportingService = new ConsoleErrorReportingService(),
    recoveryStrategies: ErrorRecoveryStrategy[] = []
  ) {
    this.logger = logger;
    this.reportingService = reportingService;
    this.recoveryStrategies = recoveryStrategies;
  }

  handle(error: AppError | Error, context?: ErrorContext): void {
    // Log the error
    if (error instanceof AppError) {
      this.logger.log(error, context);
    } else {
      this.logger.logError(error, context);
    }

    // Report the error
    this.reportingService.reportError(error, context).catch(reportError => {
      console.warn('Failed to report error:', reportError);
    });

    // Attempt recovery for operational errors
    if (error instanceof AppError && error.isOperational) {
      this.attemptRecovery(error, context);
    }

    // Handle non-operational errors (programming errors)
    if (error instanceof AppError && !error.isOperational) {
      this.handleNonOperationalError(error, context);
    }
  }

  async handleAsync<T>(promise: Promise<T>, context?: ErrorContext): Promise<T> {
    try {
      return await promise;
    } catch (error) {
      this.handle(error as AppError | Error, context);
      throw error;
    }
  }

  isOperational(error: AppError | Error): boolean {
    return errorUtils.isOperational(error);
  }

  private async attemptRecovery(error: AppError, context?: ErrorContext): Promise<void> {
    for (const strategy of this.recoveryStrategies) {
      if (strategy.canRecover(error)) {
        try {
          await strategy.recover(error, context);
           
          return;
        } catch (recoveryError) {
          console.warn('Error recovery failed:', recoveryError);
        }
      }
    }
  }

  private handleNonOperationalError(error: AppError, context?: ErrorContext): void {
    // For non-operational errors, we might want to:
    // 1. Show a generic error message to the user
    // 2. Log detailed information for debugging
    // 3. Potentially restart the application or reload the page
    
    console.error('Non-operational error detected:', error);
    
    // In a production app, you might want to:
    // - Show a user-friendly error page
    // - Send error to monitoring service
    // - Restart the application if necessary
  }

  // Retry mechanism for async operations
  async retry<T>(
    operation: () => Promise<T>,
    context?: ErrorContext,
    maxAttempts: number = this.maxRetryAttempts
  ): Promise<T> {
    let lastError: AppError | Error;
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error as AppError | Error;
        
        if (error instanceof AppError && errorUtils.shouldRetry(error, attempt, maxAttempts)) {
          const delay = errorUtils.getRetryDelay(attempt);
           
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        
        break;
      }
    }
    
    this.handle(lastError!, context);
    throw lastError!;
  }

  // Add recovery strategy
  addRecoveryStrategy(strategy: ErrorRecoveryStrategy): void {
    this.recoveryStrategies.push(strategy);
  }

  // Set logger
  setLogger(logger: ErrorLogger): void {
    this.logger = logger;
  }

  // Set reporting service
  setReportingService(service: ErrorReportingService): void {
    this.reportingService = service;
  }

  // Get reporting service
  getReportingService(): ErrorReportingService {
    return this.reportingService;
  }

  // Get error statistics
  getErrorStats(): { total: number; byCategory: Record<string, number>; bySeverity: Record<string, number> } {
    // This would typically come from your error storage/logging system
    return {
      total: 0,
      byCategory: {},
      bySeverity: {}
    };
  }
}

// Global error handler instance
export const globalErrorHandler = new AppErrorHandler(
  new ConsoleErrorLogger(),
  new ConsoleErrorReportingService(),
  [
    new NetworkErrorRecoveryStrategy(),
    new AuthenticationErrorRecoveryStrategy()
  ]
);

// Error boundary utilities
export const errorBoundaryUtils = {
  // Handle errors in async operations
  handleAsync: <T>(promise: Promise<T>, context?: ErrorContext): Promise<T> => {
    return globalErrorHandler.handleAsync(promise, context);
  },

  // Retry operations with exponential backoff
  retry: <T>(
    operation: () => Promise<T>,
    context?: ErrorContext,
    maxAttempts?: number
  ): Promise<T> => {
    return globalErrorHandler.retry(operation, context, maxAttempts);
  },

  // Log error with context
  logError: (error: AppError | Error, context?: ErrorContext): void => {
    globalErrorHandler.handle(error, context);
  },

  // Report error to external service
  reportError: (error: AppError | Error, context?: ErrorContext): Promise<void> => {
    return globalErrorHandler.getReportingService().reportError(error, context);
  },

  // Set user context for error reporting
  setUser: (userId: string, userInfo?: Record<string, any>): void => {
    globalErrorHandler.getReportingService().setUser(userId, userInfo);
  },

  // Set global context for error reporting
  setContext: (context: Record<string, any>): void => {
    globalErrorHandler.getReportingService().setContext(context);
  }
}; 